'use client';

import * as React from 'react';

import Link from 'next/link';

import * as NavPrimitive from '@radix-ui/react-tabs';
import type { ClassValue, VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { cn } from '@avodah-inc/ui/utils';

export const navVariants = tv({
  slots: {
    root: 'inline-flex w-full',
    list: 'inline-flex flex-col text-left w-full gap-[0.125rem]',
    item: 'group line-clamp-1 inline-flex flex-nowrap w-full h-fit box-border select-none justify-start items-center text-left outline-none text-[1rem] leading-[1.125rem] font-normal text-gray-500 rounded p-3 gap-3 cursor-pointer',
    icon: 'h-full flex justify-center items-center text-base-white',
    label: 'h-full text-base-white items-center',
    subLabel: 'text-base-white',
  },
  variants: {
    variant: {
      default: {
        item: 'bg-transparent hover:text-base-white border border-transparent hover:border-onDark-high data-[state=active]:border data-[state=active]:border-onDark-high data-[state=active]:text-onDark-high',
        label: 'text-sm leading-5 font-semibold',
      },
      innerNav: {
        item: 'border-none text-primary hover:text-primary-500 data-[state=active]:bg-primary-50 data-[state=active]:text-primary-500 pr-6',
        icon: 'h-full items-start text-gray-500 group-data-[state=active]:text-primary group-hover:text-primary',
        label:
          'text-gray-500 group-hover:text-primary group-data-[state=active]:text-base-black text-xl leading-5 font-semibold',
        subLabel:
          'text-md text-gray-500 group-hover:text-primary group-data-[state=active]:text-base-black leading-6 font-semibold',
      },
    },
  },
  defaultVariants: {
    list: 'default',
    variant: 'default',
  },
});

type AdditionalSharedProps = {
  options: {
    icon: JSX.Element;
    label: string;
    subLabel?: string;
    value: string;
    link?: string;
    dataTestId?: string;
    disabled?: boolean;
  }[];
  classNames?: {
    root?: ClassValue;
    list?: ClassValue;
    item?: ClassValue;
    icon?: ClassValue;
    label?: ClassValue;
    subLabel?: ClassValue;
  };
  showLabels?: boolean;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
};

type NavItemProps = NavPrimitive.TabsProps &
  VariantProps<typeof navVariants> &
  AdditionalSharedProps;

const NavList = React.forwardRef<HTMLDivElement, NavItemProps>(
  ({ showLabels = true, disabled, ...props }, ref) => {
    const {
      list,
      item: itemStyles,
      icon: iconStyles,
      label: labelStyles,
      subLabel: subLabelStyles,
    } = navVariants({
      variant: props.variant,
    });
    return (
      <NavPrimitive.List
        aria-label="Tab List Group"
        className={list({
          className: props.classNames?.list,
        })}
        ref={ref}
      >
        {props.options.map((option) => (
          <Link
            key={option.value}
            href={disabled ? '#' : (option.link ?? '')}
            passHref
          >
            <NavPrimitive.Trigger
              data-state={
                (props.value || props.defaultValue) === option.value
                  ? 'active'
                  : 'inactive'
              }
              className={itemStyles({
                className: cn(
                  props.classNames?.item,
                  option.disabled && 'cursor-not-allowed',
                ),
              })}
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              onClick={() => {
                props.onValueChange?.(option.value);
              }}
              data-testid={option.dataTestId}
            >
              <div
                className={iconStyles({
                  className: props.classNames?.icon,
                })}
              >
                {option.icon}
              </div>
              {showLabels && (
                <div className="flex flex-col gap-y-[0.313rem]">
                  <div
                    className={labelStyles({
                      className: props.classNames?.label,
                    })}
                  >
                    {option.label}
                  </div>
                  {option.subLabel && (
                    <div
                      className={subLabelStyles({
                        className: props.classNames?.subLabel,
                      })}
                    >
                      {option.subLabel}
                    </div>
                  )}
                </div>
              )}
            </NavPrimitive.Trigger>
          </Link>
        ))}
      </NavPrimitive.List>
    );
  },
);

NavList.displayName = 'NavList';

export type NavProps = NavPrimitive.TabsProps &
  VariantProps<typeof navVariants> &
  AdditionalSharedProps;

export const Nav = React.forwardRef<HTMLDivElement, NavProps>(
  ({ ...props }, ref) => {
    const { root } = navVariants();
    return (
      <NavPrimitive.Root className={cn(root())} ref={ref} {...props}>
        <NavList {...props} />
      </NavPrimitive.Root>
    );
  },
);

Nav.displayName = 'Nav';
